import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { ASC } from 'app/shared/util/pagination.constants';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { createEntitySlice, EntityState, IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { defaultValue, IDepositTransaction } from 'app/shared/model/deposit-transaction.model';

const initialState: EntityState<IDepositTransaction> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/deposit';

// Actions

export const getEntities = createAsyncThunk(
  'depositTransaction/fetch_entity_list',
  async ({ sort, page, size }: IQueryParams) => {
    const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&` : ''}cacheBuster=${new Date().getTime()}`;
    return axios.get<IDepositTransaction[]>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'depositTransaction/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IDepositTransaction>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'depositTransaction/create_entity',
  async (entity: IDepositTransaction, thunkAPI) => {
    const result = await axios.post<IDepositTransaction>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'depositTransaction/update_entity',
  async (entity: IDepositTransaction, thunkAPI) => {
    const result = await axios.put<IDepositTransaction>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const approvedTransaction = createAsyncThunk(
  'depositTransaction/accept',
  async (props: any, thunkAPI) => {
    const result = await axios.put(`${apiUrl}/${props.id}/${props.isApproved}`);
    thunkAPI.dispatch(getEntities({ sort: 'createdDate,desc' }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'depositTransaction/partial_update_entity',
  async (entity: IDepositTransaction, thunkAPI) => {
    const result = await axios.patch<IDepositTransaction>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'depositTransaction/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IDepositTransaction>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const createPayment = createAsyncThunk(
  'depositTransaction/create_payment',
  async (body: any, thunkAPI) => {
    const result = await axios.post<IDepositTransaction>(apiUrl, body);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);
// slice

export const DepositTransactionSlice = createEntitySlice({
  name: 'depositTransaction',
  initialState,
  reducers: {
    updateStatus(state, action) {
      const { code, isApproved, expired } = action.payload;
      return {
        ...state,
        entities: state.entities.map(x => {
          if (x.code === code) {
            return {
              ...x,
              isApproved,
              expired,
            };
          }
          return x;
        }),
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['X-Total-Count'], 10);
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isPending(approvedTransaction), (state, action) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          loading: false,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
          entities: data.sort((a, b) => {
            if (!action.meta?.arg?.sort) {
              return 1;
            }
            const order = action.meta.arg.sort.split(',')[1];
            const predicate = action.meta.arg.sort.split(',')[0];
            return order === ASC ? (a[predicate] < b[predicate] ? -1 : 1) : b[predicate] < a[predicate] ? -1 : 1;
          }),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset, updateStatus } = DepositTransactionSlice.actions;

// Reducer
export default DepositTransactionSlice.reducer;
